import React, { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { BasicContentSpan } from "../../../../../../../Theme/basicElements";
import { CloseButtonBlock } from "../../../../../../../Theme/modalsElements";
import BasicButton from "../../../../../../../UI/BasicButton";
import BasicRadioRow from "../../../../../../../UI/BasicRadioRow";
import RemoveCrossButton from "../../../../../../../UI/RemoveCrossButton";
import {
  RatioModalForm,
  RatioModalInputBlock,
  RatioModalRadioBlock,
  RatioModalTitle,
  RatioModalValueInput,
  RatioModalWarning,
  RatioModalWrapper,
} from "./styled";

interface ITasksRatioModalProps {
  handleSetRatio: (
    type: string,
    valueRatio: number | null,
    ratioKey: string | null,
  ) => void;
  ratioKey: string | null;
  onClose: () => void;
}

const TasksRatioModal = forwardRef(
  (
    { handleSetRatio, ratioKey, onClose }: ITasksRatioModalProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [ratioValue, setRatioValue] = useState<null | number>(null);
    const [ratioType, setRatioType] = useState("percent");

    const handleChangeRatioValue = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const raw = event.target.value;

      // Разрешаем временно пустую строку
      if (raw === "") {
        setRatioValue(null);
        return;
      }

      // Разрешаем временно "-" (кроме autofill)
      if (raw === "-" && ratioType !== "autofill") {
        setRatioValue(null);
        return;
      }

      const value = Number(raw);

      // autofill не может быть отрицательным
      if (ratioType === "autofill" && value < 0) return;

      const match = raw.match(/-?\d+/);

      setRatioValue(match ? Number(match[0]) : null);
    };

    const handleChangeRatioType = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newType = e.target.value;
      setRatioType(newType);

      // Очищаем отрицательное значение при переключении на "Equal"
      if (newType === "autofill" && ratioValue !== null && ratioValue < 0) {
        setRatioValue(null);
      }
    };

    const handleSubmit = () => {
      handleSetRatio(ratioType, ratioValue, ratioKey);
    };

    return (
      <RatioModalWrapper ref={ref} tabIndex={-1}>
        <CloseButtonBlock>
          <RemoveCrossButton onClick={onClose} />
        </CloseButtonBlock>
        <RatioModalTitle>
          {!ratioKey ? "Global ratio" : "Ratio"}
        </RatioModalTitle>
        <RatioModalWarning>
          Attention! Settings for advanced users!{" "}
          <Link to="/faq" target="_blank" rel="noopener noreferrer">
            How to use it
          </Link>
        </RatioModalWarning>
        <RatioModalForm>
          <RatioModalRadioBlock>
            <BasicRadioRow
              name="ratio"
              value="percent"
              text="Percent"
              onChange={handleChangeRatioType}
              checked={ratioType === "percent"}
              prompt='Percent - increase the number of installations by "quantity" percent daily starting from the first day of the campaign.'
            />
            <BasicRadioRow
              name="ratio"
              value="amount"
              text="Fixed number"
              onChange={handleChangeRatioType}
              checked={ratioType === "amount"}
              prompt='Fixed number - increase the number of installations by "quantity" daily starting from the first day of the campaign.'
            />
            <BasicRadioRow
              name="ratio"
              value="deviation"
              text="Deviation"
              onChange={handleChangeRatioType}
              checked={ratioType === "deviation"}
              prompt='Deviation - change the number of installations up and down to "quantity" daily starting from the first day of the campaign.'
            />
            {!ratioKey && (
              <BasicRadioRow
                name="ratio"
                value="autofill"
                onChange={handleChangeRatioType}
                text="Equal"
                checked={ratioType === "autofill"}
                prompt='Equal - fills the entire table with the specified "quantity" of  keywords/rates.'
              />
            )}
          </RatioModalRadioBlock>
          <RatioModalInputBlock>
            <RatioModalValueInput
              type="text"
              inputMode="numeric"
              min={ratioType === "autofill" ? "0" : undefined}
              value={ratioValue !== null ? ratioValue : ""}
              onChange={handleChangeRatioValue}
              placeholder="Enter quantity"
              onKeyDown={(event) => {
                const baseDisallowedKeys = ["+", "e", ",", "."];
                const disallowedKeys =
                  ratioType === "autofill"
                    ? [...baseDisallowedKeys, "-"]
                    : baseDisallowedKeys;

                if (disallowedKeys.includes(event.key)) {
                  event.preventDefault();
                  return;
                }

                // Минус: запрещён только для autofill
                if (event.key === "-" && ratioType === "autofill") {
                  event.preventDefault();
                }
              }}
            />
            <BasicContentSpan>daily</BasicContentSpan>
          </RatioModalInputBlock>
        </RatioModalForm>
        <BasicButton
          onClick={handleSubmit}
          size="lg"
          text="Apply"
          variant="blue"
        />
      </RatioModalWrapper>
    );
  },
);

export default TasksRatioModal;
